import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import { PublicRoomSystem, WithPagination } from "@classview/shared-types";
import {
  UseQueryOptions,
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";

export const getSessionRoomSystems = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
  sessionStart,
  sessionEnd,
}: {
  organisationId: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
  sessionStart?: string;
  sessionEnd?: string;
}): Promise<WithPagination<PublicRoomSystem>> => {
  const offset = pageIndex * pageSize;
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  const sessionStartParam = sessionStart
    ? `&sessionStart=${encodeURIComponent(sessionStart)}`
    : "";
  const sessionEndParam = sessionEnd
    ? `&sessionEnd=${encodeURIComponent(sessionEnd)}`
    : "";

  const response = await axiosClient.get(
    `${organisationId}/room-systems?limit=${pageSize}&offset=${offset}${searchParam}${sessionStartParam}${sessionEndParam}`,
  );
  return response.data;
};

export function getSessionRoomSystemsQueryKey({
  organisationId,
  pageSize,
  pageIndex,
  search,
}: {
  organisationId: string;
  pageSize?: string;
  pageIndex?: string;
  search?: string;
}) {
  return [
    "room-systems",
    "session",
    organisationId,
    pageSize,
    pageIndex,
    search,
  ];
}

export function useGetSessionRoomSystems(
  {
    pageSize,
    pageIndex,
    search,
  }: {
    pageSize?: number;
    pageIndex?: number;
    search?: string;
  },
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicRoomSystem>, HttpError>
  >,
) {
  const { organisation } = useOrganisation();

  return useQuery({
    queryKey: getSessionRoomSystemsQueryKey({
      organisationId: organisation.id,
      pageSize: `pagesize-${pageSize}`,
      pageIndex: `pageindex-${pageIndex}`,
      search: `search-${search || "no-search"}`,
    }),
    queryFn: () =>
      getSessionRoomSystems({
        organisationId: organisation.id,
        pageSize,
        pageIndex,
        search,
      }),
    placeholderData: keepPreviousData,

    ...queryOptions,
  });
}
